/**
 * PROJECT WORKFLOW STORE - API-Backed Version
 *
 * Zustand store with PostgreSQL backend via API services.
 * All data persisted to backend, no localStorage.
 *
 * Pattern:
 * - Optimistic local updates for fast UI
 * - Background API sync for persistence
 * - Loading/error states for async operations
 */

import type { FavoriteCandidateInfo } from '@shared/models';
import React from 'react';
import { create } from 'zustand';
import { ConfigurationService } from '~/services/configuration-service';
import { FavoritesService } from '~/services/favorites-service';
import { dehydrateProjectInputs } from '~/services/hydration-service';
import { logger } from '~/services/logging';
import { ProjectService } from '~/services/project-service';
import type {
  Configuration,
  DateInputsForm,
  DateRecord,
  DRFInputsForm,
  ExistingDebtInputForm,
  ExistingDebtProject,
  MillageInputsForm,
  ProjectDateElements,
  TaxableValueMap,
  TaxValueInputsForm,
} from '~/types';
import type { BondInterestRateSettings } from '~/types/app-settings';
import type { Candidate } from '~/types/candidate';

// ============================================
// Types
// ============================================

/**
 * Project metadata
 */
export interface ProjectMetadata {
  id: string;
  name: string;
  clientName?: string;
  fiscalYearEnd?: string;
  hasExistingDebt?: boolean;
  expectedDebtProjectCount?: number;
  verifiedDebtProjectNames?: string[];
  createdAt: string;
  updatedAt: string;
  // Project management fields
  targetCompletionDate?: string; // ISO date string for due date
  archivedAt?: string; // ISO timestamp when archived (null/undefined = active)
  // Workflow status fields (computed by backend)
  hasInputs?: boolean;
  configurationCount?: number;
  favoriteCount?: number;
  // Testing project fields
  isTestingProject?: boolean;
  testScenarioId?: string;
}

/**
 * Complete project data structure
 *
 * IMPORTANT SEPARATION:
 * - RAW FORM INPUTS: Stored in backend (source of truth)
 * - CALCULATED DATA: Generated client-side from form inputs (NOT sent to backend)
 */
export interface ProjectData {
  metadata: ProjectMetadata;

  // ========================================
  // RAW FORM INPUTS (Backend Storage)
  // ========================================
  dateInputs: DateInputsForm | null;
  taxValueInputs: TaxValueInputsForm | null;
  existingDebtInputs: ExistingDebtInputForm[] | null;
  millageInputs: MillageInputsForm | null;
  drfInputs: DRFInputsForm | null;
  bondInterestRateSettings?: BondInterestRateSettings | null;

  // ========================================
  // CALCULATED DATA (Client-Side Only)
  // ========================================
  dateRecords: DateRecord[] | null;
  dateElements: ProjectDateElements | null;
  taxableValueMap: TaxableValueMap | null;
  existingDebtProjects: ExistingDebtProject[] | null;

  // Configuration processing
  configurations: Configuration[] | null;

  // Favorites (persisted to backend)
  favorites: FavoriteCandidateInfo[] | null;
}

/**
 * Workflow step types for route guards
 */
export type WorkflowStep = 'setup' | 'existing-debt' | 'proposal' | 'review';

// ============================================
// Store Interface
// ============================================

export interface ProjectStore {
  // ========================================
  // STATE
  // ========================================
  projects: Record<string, ProjectData>; // In-memory cache
  projectList: ProjectMetadata[]; // List for project selection
  testingProjectList: ProjectMetadata[]; // List of testing projects (super admin only)
  currentProjectId: string | null;

  // Loading states
  isLoadingList: boolean;
  hasLoadedList: boolean; // Prevents infinite fetch when list is empty
  isLoadingTestingList: boolean;
  hasLoadedTestingList: boolean;
  loadingProjectId: string | null;
  savingProjectId: string | null;
  error: string | null;

  // Configuration ID transition tracking (for atomic updates)
  lastIdTransition: { clientId: string; serverId: string; timestamp: number } | null;

  // ========================================
  // ASYNC PROJECT OPERATIONS (API)
  // ========================================
  fetchProjectList: () => Promise<void>;
  fetchTestingProjectList: () => Promise<void>;
  loadProject: (id: string) => Promise<ProjectData | null>;
  createProject: (
    metadata: Omit<ProjectMetadata, 'createdAt' | 'updatedAt'>
  ) => Promise<string | null>;
  deleteProject: (projectId: string) => Promise<boolean>;
  deleteTestingProject: (projectId: string) => Promise<boolean>;
  cloneProject: (
    projectId: string,
    options: { newName: string; includeConfigurations: boolean }
  ) => Promise<string | null>;
  setCurrentProject: (projectId: string | null) => void;

  // ========================================
  // PROJECT METADATA
  // ========================================
  updateProjectMetadata: (projectId: string, updates: Partial<ProjectMetadata>) => Promise<void>;
  archiveProject: (projectId: string) => Promise<void>;
  unarchiveProject: (projectId: string) => Promise<void>;
  toggleDebtProjectVerification: (projectId: string, debtProjectName: string) => void;

  // ========================================
  // FORM INPUT SAVES (Local + API)
  // ========================================
  saveDateInputsForm: (projectId: string, inputs: DateInputsForm) => Promise<void>;
  saveTaxValueInputsForm: (projectId: string, inputs: TaxValueInputsForm) => Promise<void>;
  saveExistingDebtInputsForm: (projectId: string, inputs: ExistingDebtInputForm[]) => Promise<void>;
  saveMillageInputsForm: (projectId: string, inputs: MillageInputsForm) => Promise<void>;
  saveDRFInputsForm: (projectId: string, inputs: DRFInputsForm) => Promise<void>;
  saveBondInterestRateSettings: (
    projectId: string,
    settings: BondInterestRateSettings
  ) => Promise<void>;

  // ========================================
  // CALCULATED DATA (Local Only - Not Persisted)
  // ========================================
  saveDateRecords: (projectId: string, records: DateRecord[]) => void;
  clearDateRecords: (projectId: string) => void;
  saveDateElements: (projectId: string, elements: ProjectDateElements) => void;
  saveTaxableValueMap: (projectId: string, map: TaxableValueMap) => void;
  clearTaxableValueMap: (projectId: string) => void;
  saveExistingDebtProjects: (projectId: string, projects: ExistingDebtProject[]) => void;
  clearExistingDebtProjects: (projectId: string) => void;

  // ========================================
  // FORM INPUT READS
  // ========================================
  getDateInputsForm: (projectId: string) => DateInputsForm | null;
  getTaxValueInputsForm: (projectId: string) => TaxValueInputsForm | null;
  getExistingDebtInputsForm: (projectId: string) => ExistingDebtInputForm[] | null;
  getMillageInputsForm: (projectId: string) => MillageInputsForm | null;
  getDRFInputsForm: (projectId: string) => DRFInputsForm | null;

  // ========================================
  // CALCULATED DATA READS
  // ========================================
  getDateRecords: (projectId: string) => DateRecord[] | null;
  getDateElements: (projectId: string) => ProjectDateElements | null;
  getTaxableValueMap: (projectId: string) => TaxableValueMap | null;
  getExistingDebtProjects: (projectId: string) => ExistingDebtProject[] | null;

  // ========================================
  // CONFIGURATION MANAGEMENT
  // ========================================
  getConfigurations: (projectId: string) => Configuration[] | null;
  addConfiguration: (projectId: string, configuration: Configuration) => void;
  updateConfiguration: (projectId: string, configuration: Configuration) => void;
  removeConfiguration: (projectId: string, configurationId: string) => void;
  clearConfigurations: (projectId: string) => void;

  // ========================================
  // FAVORITES MANAGEMENT
  // ========================================
  getFavorites: (projectId: string) => FavoriteCandidateInfo[] | null;
  getFavoritesForConfig: (projectId: string, configId: string) => FavoriteCandidateInfo[];
  isFavorited: (projectId: string, configId: string, shapeId: string) => boolean;
  addFavorite: (projectId: string, configId: string, shapeId: string) => Promise<void>;
  removeFavorite: (projectId: string, configId: string, shapeId: string) => Promise<void>;

  // ========================================
  // UTILITY
  // ========================================
  hasRequiredData: (projectId: string, step: WorkflowStep) => boolean;
  getAllProjects: () => ProjectMetadata[];
  clearError: () => void;

  // Internal helper for syncing project to API
  _syncProjectToAPI: (projectId: string) => Promise<boolean>;
}

// ============================================
// Helper Functions
// ============================================

const _createInitialProjectData = (metadata: ProjectMetadata): ProjectData => ({
  metadata,
  dateInputs: null,
  taxValueInputs: null,
  existingDebtInputs: null,
  millageInputs: null,
  drfInputs: null,
  bondInterestRateSettings: null,
  dateRecords: null,
  dateElements: null,
  taxableValueMap: null,
  existingDebtProjects: null,
  configurations: null,
  favorites: null,
});

/**
 * Helper function to strip heavy data from candidates (if needed)
 */
function stripCandidateHeavyData(candidate: Candidate): Candidate {
  return candidate;
}

function _stripConfigurationHeavyData(config: Configuration): Configuration {
  if (config.candidates) {
    return {
      ...config,
      candidates: config.candidates.map(stripCandidateHeavyData) as Candidate[],
    };
  }
  return config;
}

// ============================================
// Debounce Management for API Sync
// ============================================

const SYNC_DEBOUNCE_MS = 1500; // Wait 1.5 seconds after last change before syncing
const _pendingSyncTimeouts = new Map<string, NodeJS.Timeout>();

/**
 * Tracks configuration IDs with pending CREATE operations
 * Used to prevent race conditions where UPDATE fires before CREATE completes
 */
const _pendingCreates = new Map<string, Promise<void>>();

/**
 * Maps client-generated configuration IDs to server-generated IDs
 * Used when the server generates a different ID than the client
 */
const _clientToServerIdMap = new Map<string, string>();

/**
 * Get the server ID for a client-generated configuration ID
 * Returns the original ID if no mapping exists
 */
export const getServerConfigurationId = (clientId: string): string => {
  return _clientToServerIdMap.get(clientId) || clientId;
};

/**
 * Wait for any pending create operation for a configuration to complete
 * Returns the actual server ID if the ID was changed during creation
 */
const _waitForPendingCreate = async (configId: string): Promise<string> => {
  const pendingCreate = _pendingCreates.get(configId);
  if (pendingCreate) {
    await pendingCreate;
  }
  // Return the server ID if it was mapped, otherwise return the original ID
  return _clientToServerIdMap.get(configId) || configId;
};

/**
 * Debounced sync helper - cancels pending sync and schedules new one
 */
const _debouncedSync = (projectId: string, syncFn: () => Promise<boolean>) => {
  // Cancel any pending sync for this project
  const existingTimeout = _pendingSyncTimeouts.get(projectId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  // Schedule new sync
  const timeout = setTimeout(async () => {
    _pendingSyncTimeouts.delete(projectId);
    await syncFn();
  }, SYNC_DEBOUNCE_MS);

  _pendingSyncTimeouts.set(projectId, timeout);
};

// ============================================
// Store Implementation
// ============================================

export const useProjectStore = create<ProjectStore>()((set, get) => {
  // ========================================
  // PRIVATE HELPERS (inside store closure)
  // ========================================

  /**
   * Generic form input saver - DRY helper for all form save operations.
   * Handles: project lookup, optimistic update, metadata timestamp, debounced API sync.
   * Only triggers API sync if values have actually changed.
   */
  const _saveFormInput = <T>(projectId: string, fieldName: keyof ProjectData, inputs: T): void => {
    const project = get().projects[projectId];
    if (!project) {
      logger.L2.debug('[ProjectStore] Project not found', { context: 'store', projectId });
      return;
    }

    // Compare new inputs with existing values to avoid unnecessary saves
    const existingInputs = project[fieldName] as T | undefined;
    const hasChanged =
      existingInputs === undefined || JSON.stringify(existingInputs) !== JSON.stringify(inputs);

    // Always update local state for optimistic UI updates
    set((state) => ({
      projects: {
        ...state.projects,
        [projectId]: {
          ...project,
          [fieldName]: inputs,
          metadata: {
            ...project.metadata,
            updatedAt: new Date().toISOString(),
          },
        },
      },
    }));

    // Only trigger API sync if values have actually changed
    if (hasChanged) {
      _debouncedSync(projectId, () => get()._syncProjectToAPI(projectId));
    }
  };

  return {
    // Initial state
    projects: {},
    projectList: [],
    testingProjectList: [],
    currentProjectId: null,
    isLoadingList: false,
    hasLoadedList: false,
    isLoadingTestingList: false,
    hasLoadedTestingList: false,
    loadingProjectId: null,
    savingProjectId: null,
    error: null,
    lastIdTransition: null,

    // ========================================
    // ASYNC PROJECT OPERATIONS
    // ========================================

    fetchProjectList: async () => {
      // Prevent duplicate fetches (React StrictMode calls effects twice)
      if (get().isLoadingList) {
        return;
      }

      set({ isLoadingList: true, error: null });

      try {
        const projects = await ProjectService.getProjects();
        if (projects) {
          set({ projectList: projects, isLoadingList: false, hasLoadedList: true });
        } else {
          set({ error: 'Failed to load projects', isLoadingList: false, hasLoadedList: true });
        }
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error fetching project list', {
          context: 'store',
          error: String(error),
        });
        set({ error: 'Failed to load projects', isLoadingList: false, hasLoadedList: true });
      }
    },

    fetchTestingProjectList: async () => {
      // Prevent duplicate fetches
      if (get().isLoadingTestingList) {
        return;
      }

      set({ isLoadingTestingList: true, error: null });

      try {
        const projects = await ProjectService.getTestingProjects();
        if (projects) {
          set({
            testingProjectList: projects,
            isLoadingTestingList: false,
            hasLoadedTestingList: true,
          });
        } else {
          set({
            error: 'Failed to load testing projects',
            isLoadingTestingList: false,
            hasLoadedTestingList: true,
          });
        }
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error fetching testing project list', {
          context: 'store',
          error: String(error),
        });
        set({
          error: 'Failed to load testing projects',
          isLoadingTestingList: false,
          hasLoadedTestingList: true,
        });
      }
    },

    loadProject: async (id) => {
      // Prevent duplicate fetches (React StrictMode calls effects twice)
      if (get().loadingProjectId === id) {
        return null;
      }

      const cached = get().projects[id];

      // If already cached and has data, return immediately
      if (cached?.dateInputs) {
        set({ currentProjectId: id });
        return cached;
      }

      set({ loadingProjectId: id, error: null });

      try {
        // Load project with configurations and favorites in parallel
        const [configResult, favorites] = await Promise.all([
          ConfigurationService.loadProjectWithConfigurations(id),
          FavoritesService.getFavorites(id),
        ]);

        if (!configResult) {
          set({ error: 'Project not found', loadingProjectId: null });
          return null;
        }

        const { project, configurations } = configResult;

        // Merge configurations and favorites into project
        const projectWithData: ProjectData = {
          ...project,
          configurations: configurations.length > 0 ? configurations : null,
          favorites: favorites && favorites.length > 0 ? favorites : null,
        };

        set((state) => ({
          projects: {
            ...state.projects,
            [id]: projectWithData,
          },
          currentProjectId: id,
          loadingProjectId: null,
        }));

        return projectWithData;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error loading project', {
          context: 'store',
          projectId: id,
          error: String(error),
        });
        set({ error: 'Failed to load project', loadingProjectId: null });
        return null;
      }
    },

    createProject: async (metadata) => {
      const projectId = metadata.id;
      set({ savingProjectId: projectId, error: null });

      try {
        const created = await ProjectService.createProject({
          name: metadata.name,
          clientName: metadata.clientName,
          targetCompletionDate: metadata.targetCompletionDate,
        });

        if (!created) {
          set({ error: 'Failed to create project', savingProjectId: null });
          return null;
        }

        // Update local state with the created project
        // Use created.metadata directly to ensure all fields are included
        set((state) => ({
          projects: {
            ...state.projects,
            [created.metadata.id]: created,
          },
          projectList: [
            ...state.projectList,
            created.metadata, // Use full metadata from server response
          ],
          currentProjectId: created.metadata.id,
          savingProjectId: null,
        }));

        return created.metadata.id;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error creating project', {
          context: 'store',
          error: String(error),
        });
        set({ error: 'Failed to create project', savingProjectId: null });
        return null;
      }
    },

    deleteProject: async (projectId) => {
      set({ savingProjectId: projectId, error: null });

      try {
        const success = await ProjectService.deleteProject(projectId);

        if (!success) {
          set({ error: 'Failed to delete project', savingProjectId: null });
          return false;
        }

        // Remove from local state
        const { [projectId]: _removed, ...remainingProjects } = get().projects;

        set((state) => ({
          projects: remainingProjects,
          projectList: state.projectList.filter((p) => p.id !== projectId),
          currentProjectId: state.currentProjectId === projectId ? null : state.currentProjectId,
          savingProjectId: null,
        }));

        return true;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error deleting project', {
          context: 'store',
          projectId,
          error: String(error),
        });
        set({ error: 'Failed to delete project', savingProjectId: null });
        return false;
      }
    },

    deleteTestingProject: async (projectId) => {
      set({ savingProjectId: projectId, error: null });

      try {
        const success = await ProjectService.deleteTestingProject(projectId);

        if (!success) {
          set({ error: 'Failed to delete testing project', savingProjectId: null });
          return false;
        }

        // Remove from local state (both projects and testingProjectList)
        // Use state.projects inside set() to avoid race conditions
        set((state) => {
          const { [projectId]: _removed, ...remainingProjects } = state.projects;
          return {
            projects: remainingProjects,
            projectList: state.projectList.filter((p) => p.id !== projectId),
            testingProjectList: state.testingProjectList.filter((p) => p.id !== projectId),
            currentProjectId: state.currentProjectId === projectId ? null : state.currentProjectId,
            savingProjectId: null,
          };
        });

        return true;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error deleting testing project', {
          context: 'store',
          projectId,
          error: String(error),
        });
        set({ error: 'Failed to delete testing project', savingProjectId: null });
        return false;
      }
    },

    cloneProject: async (projectId, options) => {
      set({ savingProjectId: projectId, error: null });

      try {
        // If includeConfigurations is true, we need to load the source project with configurations
        // (the local cache may not have them loaded)
        let sourceConfigurations: Configuration[] = [];
        if (options.includeConfigurations) {
          const sourceResult = await ConfigurationService.loadProjectWithConfigurations(projectId);
          if (sourceResult?.configurations) {
            sourceConfigurations = sourceResult.configurations;
          }
        }

        // Use ProjectService to clone (loads source, creates new project with cloned inputs)
        const clonedProject = await ProjectService.cloneProject(projectId, options);

        if (!clonedProject) {
          set({ error: 'Failed to clone project', savingProjectId: null });
          return null;
        }

        // Clone configurations if requested and source had any
        if (options.includeConfigurations && sourceConfigurations.length > 0) {
          logger.L2.debug('[ProjectStore] Cloning configurations', {
            context: 'store',
            sourceProjectId: projectId,
            targetProjectId: clonedProject.metadata.id,
            configurationCount: sourceConfigurations.length,
          });

          for (const config of sourceConfigurations) {
            try {
              // Create new configuration in the cloned project
              // ConfigurationService will generate new IDs
              const inputs = await import('~/services/hydration-service').then((m) =>
                m.dehydrateConfigurationInputs(config, clonedProject)
              );

              await ConfigurationService.createConfiguration(
                clonedProject.metadata.id,
                {
                  name: config.name,
                  inputs,
                },
                clonedProject
              );

              logger.L2.debug('[ProjectStore] Configuration cloned successfully', {
                context: 'store',
                sourceConfigId: config.id,
                configName: config.name,
              });
            } catch (configError) {
              logger.L2.debug('[ProjectStore] Error cloning configuration', {
                context: 'store',
                sourceConfigId: config.id,
                targetProjectId: clonedProject.metadata.id,
                error: String(configError),
              });
              // Continue cloning other configurations even if one fails
            }
          }
        }

        // Update local state with the cloned project
        set((state) => ({
          projects: {
            ...state.projects,
            [clonedProject.metadata.id]: clonedProject,
          },
          projectList: [...state.projectList, clonedProject.metadata],
          savingProjectId: null,
        }));

        return clonedProject.metadata.id;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error cloning project', {
          context: 'store',
          projectId,
          error: String(error),
        });
        set({ error: 'Failed to clone project', savingProjectId: null });
        return null;
      }
    },

    setCurrentProject: (projectId) => {
      set({ currentProjectId: projectId });
    },

    // ========================================
    // PROJECT METADATA
    // ========================================

    updateProjectMetadata: async (projectId, updates) => {
      const project = get().projects[projectId];
      const updatedAt = new Date().toISOString();

      // Optimistic update - update both full project cache and list views
      set((state) => {
        const newState: Partial<ProjectStore> = {
          // Update projectList so the table view reflects changes immediately
          projectList: state.projectList.map((p) =>
            p.id === projectId
              ? {
                  ...p,
                  ...updates,
                  updatedAt,
                }
              : p
          ),
          // Update testingProjectList as well
          testingProjectList: state.testingProjectList.map((p) =>
            p.id === projectId
              ? {
                  ...p,
                  ...updates,
                  updatedAt,
                }
              : p
          ),
        };

        // Also update full project cache if loaded
        if (project) {
          newState.projects = {
            ...state.projects,
            [projectId]: {
              ...project,
              metadata: {
                ...project.metadata,
                ...updates,
                updatedAt,
              },
            },
          };
        }

        return newState as ProjectStore;
      });

      // Sync to API - call directly since we may not have full project loaded
      try {
        const result = await ProjectService.updateProject(projectId, {
          name: updates.name,
          clientName: updates.clientName,
          targetCompletionDate: updates.targetCompletionDate ?? null,
          archivedAt: updates.archivedAt ?? null,
        });

        if (!result) {
          logger.L2.debug('[ProjectStore] Failed to update project metadata', {
            context: 'store',
            projectId,
          });
        }
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error updating project metadata', {
          context: 'store',
          projectId,
          error: String(error),
        });
      }
    },

    archiveProject: async (projectId) => {
      await get().updateProjectMetadata(projectId, {
        archivedAt: new Date().toISOString(),
      });
    },

    unarchiveProject: async (projectId) => {
      await get().updateProjectMetadata(projectId, {
        archivedAt: undefined,
      });
    },

    toggleDebtProjectVerification: (projectId, debtProjectName) => {
      const project = get().projects[projectId];
      if (!project) return;

      const currentVerified = project.metadata.verifiedDebtProjectNames || [];
      const isVerified = currentVerified.includes(debtProjectName);
      const updatedVerified = isVerified
        ? currentVerified.filter((name) => name !== debtProjectName)
        : [...currentVerified, debtProjectName];

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            metadata: {
              ...project.metadata,
              verifiedDebtProjectNames: updatedVerified,
              updatedAt: new Date().toISOString(),
            },
          },
        },
      }));

      // Note: verifiedDebtProjectNames is local-only metadata, not synced to API
    },

    // ========================================
    // FORM INPUT SAVES (Local + API)
    // Uses _saveFormInput helper for DRY implementation
    // ========================================

    saveDateInputsForm: async (projectId, inputs) =>
      _saveFormInput(projectId, 'dateInputs', inputs),
    saveTaxValueInputsForm: async (projectId, inputs) =>
      _saveFormInput(projectId, 'taxValueInputs', inputs),
    saveExistingDebtInputsForm: async (projectId, inputs) =>
      _saveFormInput(projectId, 'existingDebtInputs', inputs),
    saveMillageInputsForm: async (projectId, inputs) =>
      _saveFormInput(projectId, 'millageInputs', inputs),
    saveDRFInputsForm: async (projectId, inputs) => _saveFormInput(projectId, 'drfInputs', inputs),
    saveBondInterestRateSettings: async (projectId, settings) =>
      _saveFormInput(projectId, 'bondInterestRateSettings', settings),

    // ========================================
    // CALCULATED DATA (Local Only)
    // ========================================

    saveDateRecords: (projectId, records) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            dateRecords: records,
          },
        },
      }));
    },

    clearDateRecords: (projectId) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            dateRecords: null,
          },
        },
      }));
    },

    saveDateElements: (projectId, elements) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            dateElements: elements,
          },
        },
      }));
    },

    saveTaxableValueMap: (projectId, map) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            taxableValueMap: map,
          },
        },
      }));
    },

    clearTaxableValueMap: (projectId) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            taxableValueMap: null,
          },
        },
      }));
    },

    saveExistingDebtProjects: (projectId, projects) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            existingDebtProjects: projects,
          },
        },
      }));
    },

    clearExistingDebtProjects: (projectId) => {
      const project = get().projects[projectId];
      if (!project) return;

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            existingDebtProjects: null,
          },
        },
      }));
    },

    // ========================================
    // FORM INPUT READS
    // ========================================

    getDateInputsForm: (projectId) => get().projects[projectId]?.dateInputs || null,
    getTaxValueInputsForm: (projectId) => get().projects[projectId]?.taxValueInputs || null,
    getExistingDebtInputsForm: (projectId) => get().projects[projectId]?.existingDebtInputs || null,
    getMillageInputsForm: (projectId) => get().projects[projectId]?.millageInputs || null,
    getDRFInputsForm: (projectId) => get().projects[projectId]?.drfInputs || null,

    // ========================================
    // CALCULATED DATA READS
    // ========================================

    getDateRecords: (projectId) => get().projects[projectId]?.dateRecords || null,
    getDateElements: (projectId) => get().projects[projectId]?.dateElements || null,
    getTaxableValueMap: (projectId) => get().projects[projectId]?.taxableValueMap || null,
    getExistingDebtProjects: (projectId) => get().projects[projectId]?.existingDebtProjects || null,

    // ========================================
    // CONFIGURATION MANAGEMENT
    // ========================================

    getConfigurations: (projectId) => get().projects[projectId]?.configurations || null,

    addConfiguration: (projectId, configuration) => {
      const project = get().projects[projectId];
      if (!project) return;

      const currentConfigurations = project.configurations || [];
      const updatedConfigurations = [...currentConfigurations, configuration];

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            configurations: updatedConfigurations,
          },
        },
      }));

      // Create new configuration in API (IMMEDIATE - not debounced)
      // Must be immediate to ensure config exists before any updates are attempted
      // Track the pending create so updateConfiguration can wait for it
      const clientGeneratedId = configuration.id;
      const createPromise = (async () => {
        const currentProject = get().projects[projectId];
        if (!currentProject) return;

        try {
          const inputs = await import('~/services/hydration-service').then((m) =>
            m.dehydrateConfigurationInputs(configuration, currentProject)
          );

          const createdConfig = await ConfigurationService.createConfiguration(
            projectId,
            { name: configuration.name, inputs },
            currentProject
          );

          // If creation succeeded, update the store with the server-generated ID
          // The server may generate a different ID than the client-generated one
          if (createdConfig && createdConfig.id !== clientGeneratedId) {
            // Track the ID mapping for future lookups
            _clientToServerIdMap.set(clientGeneratedId, createdConfig.id);

            // ATOMIC UPDATE: configurations array AND lastIdTransition in ONE set() call
            // This ensures React sees both changes in the same render cycle,
            // preventing the flash where selection points to server ID but array still has client ID
            set((state) => {
              const currentProject = state.projects[projectId];
              if (!currentProject?.configurations) return state;

              // IMPORTANT: Only update the ID, preserve client-side generated data (candidates, etc.)
              // The server response doesn't include candidates - those were generated client-side
              const updatedConfigurations = currentProject.configurations.map((config) =>
                config.id === clientGeneratedId
                  ? { ...config, id: createdConfig.id } // Only update ID, keep candidates
                  : config
              );

              return {
                ...state,
                projects: {
                  ...state.projects,
                  [projectId]: {
                    ...currentProject,
                    configurations: updatedConfigurations,
                  },
                },
                lastIdTransition: {
                  clientId: clientGeneratedId,
                  serverId: createdConfig.id,
                  timestamp: Date.now(),
                },
              };
            });

            // Update pending creates map to use the server ID
            if (_pendingCreates.has(clientGeneratedId)) {
              _pendingCreates.set(createdConfig.id, _pendingCreates.get(clientGeneratedId)!);
              _pendingCreates.delete(clientGeneratedId);
            }
          }
        } catch (error) {
          logger.L2.debug('[ProjectStore] Error creating configuration', {
            context: 'store',
            projectId,
            configurationId: clientGeneratedId,
            error: String(error),
          });
        } finally {
          // Remove from pending creates when done (success or failure)
          _pendingCreates.delete(clientGeneratedId);
        }
      })();

      // Track this as a pending create
      _pendingCreates.set(clientGeneratedId, createPromise);
    },

    updateConfiguration: (projectId, updatedConfiguration) => {
      const project = get().projects[projectId];
      if (!project?.configurations) return;

      const updatedConfigurations = project.configurations.map((config) =>
        config.id === updatedConfiguration.id ? updatedConfiguration : config
      );

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            configurations: updatedConfigurations,
          },
        },
      }));

      // Sync updated configuration to API (debounced)
      // IMPORTANT: Wait for any pending create to complete first to prevent 404 errors
      const clientConfigId = updatedConfiguration.id;
      _debouncedSync(`${projectId}-config-${clientConfigId}`, async () => {
        // Wait for pending create operation to complete (if any)
        // This ensures the configuration exists in the database before updating
        // Returns the actual server ID if the ID was changed during creation
        const serverConfigId = await _waitForPendingCreate(clientConfigId);

        const currentProject = get().projects[projectId];
        if (!currentProject) return false;

        // Get the configuration from store using the server ID (if ID was changed)
        const currentConfig = currentProject.configurations?.find((c) => c.id === serverConfigId);

        if (!currentConfig) return false;

        // Merge the updates with the current config, ensuring we use the server-generated ID
        const configToSave = {
          ...currentConfig,
          ...updatedConfiguration,
          // Use the server-generated ID (which is what exists in the database)
          id: serverConfigId,
        };

        const result = await ConfigurationService.saveConfiguration(
          projectId,
          configToSave,
          currentProject
        );
        return result !== null;
      });
    },

    removeConfiguration: (projectId, configurationId) => {
      const project = get().projects[projectId];
      if (!project?.configurations) return;

      // IMPORTANT: Cancel any pending debounced create/update for this configuration
      // This prevents a race condition where delete fires before the debounced create
      const syncKey = `${projectId}-config-${configurationId}`;
      const pendingTimeout = _pendingSyncTimeouts.get(syncKey);
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
        _pendingSyncTimeouts.delete(syncKey);
        logger.L2.debug('[ProjectStore] Cancelled pending sync for deleted configuration', {
          context: 'store',
          projectId,
          configurationId,
        });
      }

      // Also remove from pending creates (create will complete but we don't care about the result)
      _pendingCreates.delete(configurationId);

      const updatedConfigurations = project.configurations.filter(
        (config) => config.id !== configurationId
      );

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            configurations: updatedConfigurations.length > 0 ? updatedConfigurations : null,
          },
        },
      }));

      // Delete configuration from API (immediate, not debounced)
      ConfigurationService.deleteConfiguration(projectId, configurationId).catch((error) => {
        logger.L2.debug('[ProjectStore] Error deleting configuration from API', {
          context: 'store',
          projectId,
          configurationId,
          error: String(error),
        });
      });
    },

    clearConfigurations: (projectId) => {
      const project = get().projects[projectId];
      if (!project) return;

      // Cancel any pending syncs and creates for all configurations being cleared
      if (project.configurations) {
        for (const config of project.configurations) {
          const syncKey = `${projectId}-config-${config.id}`;
          const pendingTimeout = _pendingSyncTimeouts.get(syncKey);
          if (pendingTimeout) {
            clearTimeout(pendingTimeout);
            _pendingSyncTimeouts.delete(syncKey);
          }
          // Also clean up pending creates
          _pendingCreates.delete(config.id);
        }
      }

      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            configurations: null,
          },
        },
      }));
    },

    // ========================================
    // FAVORITES MANAGEMENT
    // ========================================

    getFavorites: (projectId) => get().projects[projectId]?.favorites || null,

    getFavoritesForConfig: (projectId, configId) => {
      const favorites = get().projects[projectId]?.favorites;
      if (!favorites) return [];
      return favorites.filter((f) => f.configurationId === configId);
    },

    isFavorited: (projectId, configId, shapeId) => {
      const favorites = get().projects[projectId]?.favorites;
      if (!favorites) return false;
      return favorites.some((f) => f.configurationId === configId && f.shapeId === shapeId);
    },

    addFavorite: async (projectId, configId, shapeId) => {
      const project = get().projects[projectId];
      if (!project) return;

      // Check if already favorited (idempotent)
      if (get().isFavorited(projectId, configId, shapeId)) {
        return;
      }

      // Optimistic update with temporary ID
      const tempFavorite: FavoriteCandidateInfo = {
        id: `temp-${Date.now()}`,
        projectId,
        configurationId: configId,
        shapeId,
        createdAt: new Date().toISOString(),
      };

      const currentFavorites = project.favorites || [];
      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            favorites: [...currentFavorites, tempFavorite],
          },
        },
      }));

      // Sync to API
      try {
        const result = await FavoritesService.addFavorite(projectId, configId, shapeId);
        if (result) {
          // Replace temp with real favorite
          const updatedProject = get().projects[projectId];
          if (updatedProject) {
            const updatedFavorites = (updatedProject.favorites || []).map((f) =>
              f.id === tempFavorite.id ? result : f
            );
            set((state) => ({
              projects: {
                ...state.projects,
                [projectId]: {
                  ...updatedProject,
                  favorites: updatedFavorites,
                },
              },
            }));
          }
        }
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error adding favorite', {
          context: 'store',
          projectId,
          configId,
          shapeId,
          error: String(error),
        });
        // Revert optimistic update on error
        const revertProject = get().projects[projectId];
        if (revertProject) {
          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...revertProject,
                favorites: (revertProject.favorites || []).filter((f) => f.id !== tempFavorite.id),
              },
            },
          }));
        }
      }
    },

    removeFavorite: async (projectId, configId, shapeId) => {
      const project = get().projects[projectId];
      if (!project?.favorites) return;

      // Find the favorite to remove
      const favoriteToRemove = project.favorites.find(
        (f) => f.configurationId === configId && f.shapeId === shapeId
      );
      if (!favoriteToRemove) return;

      // Optimistic update
      set((state) => ({
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            favorites: project.favorites!.filter(
              (f) => !(f.configurationId === configId && f.shapeId === shapeId)
            ),
          },
        },
      }));

      // Sync to API
      try {
        const success = await FavoritesService.removeFavorite(projectId, configId, shapeId);
        if (!success) {
          // Revert if API failed
          const revertProject = get().projects[projectId];
          if (revertProject) {
            set((state) => ({
              projects: {
                ...state.projects,
                [projectId]: {
                  ...revertProject,
                  favorites: [...(revertProject.favorites || []), favoriteToRemove],
                },
              },
            }));
          }
        }
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error removing favorite', {
          context: 'store',
          projectId,
          configId,
          shapeId,
          error: String(error),
        });
        // Revert optimistic update on error
        const revertProject = get().projects[projectId];
        if (revertProject) {
          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...revertProject,
                favorites: [...(revertProject.favorites || []), favoriteToRemove],
              },
            },
          }));
        }
      }
    },

    // ========================================
    // UTILITY
    // ========================================

    hasRequiredData: (projectId, step) => {
      const project = get().projects[projectId];
      if (!project) return false;

      switch (step) {
        case 'setup':
          return true;
        case 'existing-debt':
          return !!(project.dateInputs && project.taxValueInputs);
        case 'proposal':
          return !!(project.dateInputs && project.taxValueInputs);
        case 'review':
          return !!(
            project.dateRecords &&
            project.taxableValueMap &&
            project.millageInputs &&
            project.drfInputs
          );
        default:
          return false;
      }
    },

    getAllProjects: () => get().projectList,

    clearError: () => set({ error: null }),

    // ========================================
    // INTERNAL: Sync project to API
    // ========================================

    _syncProjectToAPI: async (projectId) => {
      const project = get().projects[projectId];
      if (!project) return false;

      set({ savingProjectId: projectId });

      try {
        const inputs = dehydrateProjectInputs(project);

        const result = await ProjectService.updateProject(projectId, {
          name: project.metadata.name,
          clientName: project.metadata.clientName,
          targetCompletionDate: project.metadata.targetCompletionDate ?? null,
          archivedAt: project.metadata.archivedAt ?? null,
          inputs,
        });

        set({ savingProjectId: null });

        if (!result) {
          logger.L2.debug('[ProjectStore] Failed to sync project to API', {
            context: 'store',
            projectId,
          });
          return false;
        }

        return true;
      } catch (error) {
        logger.L2.debug('[ProjectStore] Error syncing project to API', {
          context: 'store',
          projectId,
          error: String(error),
        });
        set({ savingProjectId: null });
        return false;
      }
    },
  };
});

// ============================================
// Hooks
// ============================================

/**
 * Hook to load project list on mount
 */
export const useProjectList = () => {
  const { projectList, isLoadingList, hasLoadedList, fetchProjectList, error } = useProjectStore();

  React.useEffect(() => {
    // Only fetch if we haven't loaded yet and aren't currently loading
    if (!hasLoadedList && !isLoadingList) {
      fetchProjectList();
    }
  }, [hasLoadedList, isLoadingList, fetchProjectList]);

  return { projectList, isLoading: isLoadingList, hasLoadedList, error };
};

/**
 * Hook to load testing project list on mount (super admin only)
 * @param isSuperAdmin - Whether the current user is a super admin. Fetch is skipped if false.
 */
export const useTestingProjectList = (isSuperAdmin: boolean) => {
  const {
    testingProjectList,
    isLoadingTestingList,
    hasLoadedTestingList,
    fetchTestingProjectList,
    error,
  } = useProjectStore();

  React.useEffect(() => {
    // Only fetch if super admin, haven't loaded yet, and aren't currently loading
    if (isSuperAdmin && !hasLoadedTestingList && !isLoadingTestingList) {
      fetchTestingProjectList();
    }
  }, [isSuperAdmin, hasLoadedTestingList, isLoadingTestingList, fetchTestingProjectList]);

  return {
    testingProjectList,
    isLoading: isLoadingTestingList,
    hasLoadedTestingList,
    error,
  };
};

/**
 * Hook to load a specific project
 */
export const useProject = (projectId: string | null) => {
  const { projects, loadingProjectId, loadProject, error } = useProjectStore();

  React.useEffect(() => {
    if (!projectId || loadingProjectId === projectId) return;

    const cached = projects[projectId];
    // Only skip fetch if cached AND has actual data (not just metadata from createProject)
    const hasData = cached?.dateInputs !== undefined;

    if (!hasData) {
      loadProject(projectId);
    }
  }, [projectId, projects, loadingProjectId, loadProject]);

  return {
    project: projectId ? projects[projectId] : null,
    isLoading: loadingProjectId === projectId,
    error,
  };
};

/**
 * Hook to check if store is ready (for SSR compatibility)
 * With API-based store, always ready after initial render
 */
export const useHydrateProjectStore = () => {
  const [hasHydrated, setHasHydrated] = React.useState(false);

  React.useEffect(() => {
    setHasHydrated(true);
  }, []);

  return hasHydrated;
};
